import { useState, useEffect } from 'react';
import type { Insight, InsightCategory, SourceType } from '@claude-code-insights/shared';
import { INSIGHT_CATEGORIES, SOURCE_TYPES } from '@claude-code-insights/shared';
import { fetchInsights } from '../lib/api';
import { InsightCard } from '../components/InsightCard';

export function FeedView() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);

  // Filters
  const [category, setCategory] = useState<InsightCategory | ''>('');
  const [sourceType, setSourceType] = useState<SourceType | ''>('');
  const [minRelevance, setMinRelevance] = useState<number>(0);

  async function loadInsights(append = false) {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchInsights({
        category: category || undefined,
        sourceType: sourceType || undefined,
        minRelevance: minRelevance || undefined,
        limit: 20,
        cursor: append ? cursor ?? undefined : undefined,
      });
      setInsights((prev) => (append ? [...prev, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInsights();
  }, [category, sourceType, minRelevance]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Recent Insights</h2>
      </div>

      {/* Filter bar */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as InsightCategory | '')}
          className="input text-sm"
        >
          <option value="">All categories</option>
          {INSIGHT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value as SourceType | '')}
          className="input text-sm"
        >
          <option value="">All sources</option>
          {SOURCE_TYPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={minRelevance}
          onChange={(e) => setMinRelevance(Number(e.target.value))}
          className="input text-sm"
        >
          <option value={0}>Any relevance</option>
          <option value={5}>5+ relevance</option>
          <option value={7}>7+ relevance</option>
          <option value={9}>9+ relevance</option>
        </select>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-3 bg-obsidian-error/10 border border-obsidian-error/20 rounded-lg text-sm text-obsidian-error">
          {error}
        </div>
      )}

      {/* Insights list */}
      <div className="space-y-3">
        {insights.map((insight) => (
          <InsightCard key={insight.insightId} insight={insight} />
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="py-8 text-center text-obsidian-text-muted">
          Loading...
        </div>
      )}

      {/* Empty state */}
      {!loading && insights.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-obsidian-text-muted text-sm">No insights yet</p>
          <p className="text-obsidian-text-faint text-xs mt-2">
            Send a link to your Telegram bot to get started
          </p>
        </div>
      )}

      {/* Load more */}
      {cursor && !loading && (
        <div className="mt-6 text-center">
          <button
            onClick={() => loadInsights(true)}
            className="btn-ghost text-sm"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
